import { useQueryClient } from '@tanstack/react-query'
import { EditTariffFormModelDto } from './dto/tariffs-form.dto'
import { TariffDto } from './dto/tariffs.dto'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import axios from 'axios'
import { editTariffApiErrors } from './dto/tariffs-api-errors'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

interface UseEditTariffHook {
  isLoading: boolean
  editTariff: (payload: EditTariffFormModelDto) => void
}

/**
 * Function for edit tariff data
 * @param payload - data off TariffFormModelDto added to mutate react-query function
 * @param onClose - function for closing dialog on success method
 * @param installationId - unique installation identifier as a string
 * @returns isLoading as boolean and isError as boolean
 * @returns mutate function from react-query
 */
export const useEditTariff = (onClose: () => void, installationId: number): UseEditTariffHook => {
  const { t } = useCustomTranslation()
  const authFetch = useAuthFetchWithResult()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const editTariffApi = async (payload: EditTariffFormModelDto): Promise<ApiResult<string | TariffDto>> => {
    const tariffResponse = await authFetch<TariffDto>({
      path: 'tariffs',
      endpoint: process.env.REACT_APP_HAGER_BRIDGE_SERVICE,
      method: 'PUT',
      payload: payload,
    })

    return new ApiResult(tariffResponse)
  }

  const { isPending, mutate: editTariff } = usePortalMutation({
    mutationFn: editTariffApi,
    onError: error => {
      if (axios.isAxiosError(error)) {
        enqueueErrorStack(t(editTariffApiErrors[error.response?.data.errorCode]))
      } else {
        enqueueErrorStack(t('emobility.tariffs.edit-tariff-failed'))
      }
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['tariffs', installationId] })
      await queryClient.invalidateQueries({ queryKey: ['tariffDetails', variables.tariffId] })
      await queryClient.invalidateQueries({ queryKey: ['wallboxes', installationId] })
      enqueueSuccessStack(t('emobility.tariffs.edit-tariff-success'))
      onClose()
    },
  })

  return { isLoading: isPending, editTariff }
}
